import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Home, Award, Settings, Menu, X, ChevronRight } from "lucide-react";

const Navigation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  // Breadcrumb logic
  const getBreadcrumbs = () => {
    const path = location.pathname;
    const crumbs = [{ label: "Accueil", path: "/dashboard" }];

    if (path.includes("/module/") && !path.includes("/quiz")) {
      crumbs.push({ label: "Module", path: path });
    } else if (path.includes("/quiz")) {
      crumbs.push({ label: "Module", path: path.replace("/quiz", "") });
      crumbs.push({ label: "Quiz", path: path });
    } else if (path.includes("/certificate")) {
      crumbs.push({ label: "Certificat", path: path });
    } else if (path.includes("/admin")) {
      crumbs.push({ label: "Administration", path: path });
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <>
      {/* Desktop Navigation */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Title */}
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate("/dashboard")}>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">A</span>
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-xl font-bold gradient-text">Les A.C.E.S.</h1>
                  <p className="text-xs text-gray-500">Formation Animateurs</p>
                </div>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-4">
              <Button
                variant={location.pathname === "/dashboard" ? "default" : "ghost"}
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Tableau de bord
              </Button>

              {isAdmin && (
                <Button
                  variant={location.pathname === "/admin" ? "default" : "ghost"}
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Admin
                </Button>
              )}

              <div className="flex items-center gap-3 pl-3 border-l">
                <div className="text-right hidden lg:block">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500">
                    {isAdmin ? "Administrateur" : "Animateur"}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLogout}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Breadcrumb */}
          {breadcrumbs.length > 1 && (
            <div className="pb-3 flex items-center gap-2 text-sm overflow-x-auto">
              {breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex items-center gap-2">
                  {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <button
                    onClick={() => navigate(crumb.path)}
                    className={`whitespace-nowrap ${
                      index === breadcrumbs.length - 1
                        ? "text-orange-600 font-medium"
                        : "text-gray-600 hover:text-orange-600"
                    }`}
                  >
                    {crumb.label}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed right-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            <div className="p-6 space-y-6">
              <div className="border-b pb-4">
                <p className="font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-sm text-gray-500">
                  {isAdmin ? "Administrateur" : "Animateur"}
                </p>
              </div>

              <nav className="space-y-2">
                <Button
                  variant={location.pathname === "/dashboard" ? "default" : "ghost"}
                  onClick={() => {
                    navigate("/dashboard");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-start"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Tableau de bord
                </Button>

                {isAdmin && (
                  <Button
                    variant={location.pathname === "/admin" ? "default" : "ghost"}
                    onClick={() => {
                      navigate("/admin");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Administration
                  </Button>
                )}
              </nav>

              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-start"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
